import { useEffect, useState, type ReactNode } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Button, Flex, Notification, Small } from "orcs-design-system";

import { useDesignOptionId } from "./overlay/useDesignOptionId";
import { useOverlayOptionId } from "./overlay/useOverlayOptionId";
import { useOptionWriteGuard } from "./overlay/useOptionWriteGuard";
import { useKeepDesignOptionInUrl } from "./overlay/useKeepDesignOptionInUrl";
import { getDesignOptionLabel } from "./designOptionLabels";
import {
  getOptionPageSupport,
  type OptionPageSupport,
} from "./optionPageSupport";
import { OptionPageSupportContext } from "./OptionPageSupportContext";
import { useDesignWorkspaceOptions } from "./useDesignWorkspaceOptions";
import {
  parseDesignOptionCompare,
  serializeDesignOptionCompare,
} from "./designOptionCompare";
import {
  STALE_DESIGN_OPTION_EVENT,
  takeQueuedStaleDesignOption,
} from "./overlay/staleDesignOption";

// Every live-only operation (overlayLiveOnlyMutations.ts) is outside the org
// structure; the examples are the ones a read-only page leaves in reach.
const LIVE_ONLY_WRITES_NOTE =
  "Actions outside the org structure, such as comments, slide decks, AI dashboards and your view preferences, are not part of design options and still save to the baseline.";

const readOnlyMessage = (optionName: string | undefined) =>
  `Showing baseline data, not ${optionName ?? "the selected design option"}. Edits that can't be saved to the option are disabled.`;

const useSelectedOptionName = (optionId: string): string | undefined => {
  const { options, design } = useDesignWorkspaceOptions();
  const option = options.find((item) => item.id === optionId);
  return option
    ? getDesignOptionLabel(option, design?.baselineDataDate)
    : undefined;
};

type BannerProps = {
  support: OptionPageSupport;
  optionName: string | undefined;
};

const OptionBanner = ({ support, optionName }: BannerProps) => {
  if (support === "blocked") {
    return null;
  }

  return (
    <Box mb="r" data-testid="option-page-banner" data-support={support}>
      <Notification
        variant={support === "supported" ? "informational" : "warning"}
        closable={false}
      >
        {support === "supported" ? (
          <Small>{`Editing design option: ${optionName ?? "selected option"}`}</Small>
        ) : (
          <>
            <Small display="block">{readOnlyMessage(optionName)}</Small>
            <Small display="block">{LIVE_ONLY_WRITES_NOTE}</Small>
          </>
        )}
      </Notification>
    </Box>
  );
};

type BlockedNoticeProps = { onSwitchToBaseline: () => void };

const BlockedNotice = ({ onSwitchToBaseline }: BlockedNoticeProps) => (
  <Flex flexDirection="column" gap="r" alignItems="flex-start">
    <Notification variant="warning" closable={false}>
      <Small data-testid="option-page-blocked-notice">
        This page does not support design options yet. Switch to Baseline to use
        it.
      </Small>
    </Notification>
    <Button onClick={onSwitchToBaseline}>Switch to Baseline</Button>
  </Flex>
);

type OptionPageGuardProps = { children: ReactNode };

/**
 * Mounted once in the app layout. Labels every page with the selected design
 * option and keeps pages that cannot honour an option from writing the
 * baseline. It also arms `optionWriteGuardLink`, which refuses any baseline
 * write a control failed to block.
 */
const OptionPageGuard = ({ children }: OptionPageGuardProps) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const optionId = useOverlayOptionId();
  const { setOptionId } = useDesignOptionId();
  const [staleNotice, setStaleNotice] = useState<"selected" | "compare" | null>(
    null
  );
  useOptionWriteGuard(optionId);
  useKeepDesignOptionInUrl(optionId);

  useEffect(() => {
    const onStaleOption = () => {
      const pending = takeQueuedStaleDesignOption();
      const missing = pending?.optionId;
      if (!missing) {
        return;
      }
      if (missing === optionId) {
        setOptionId(undefined);
        history.replace({
          ...history.location,
          search: serializeDesignOptionCompare(history.location.search, {
            active: false,
          }),
        });
        setStaleNotice("selected");
        return;
      }
      const compare = parseDesignOptionCompare(history.location.search);
      if (
        compare.active &&
        (missing === compare.fromOptionId || missing === compare.toOptionId)
      ) {
        history.replace({
          ...history.location,
          search: serializeDesignOptionCompare(history.location.search, {
            active: false,
          }),
        });
        setStaleNotice("compare");
      }
    };
    onStaleOption();
    window.addEventListener(STALE_DESIGN_OPTION_EVENT, onStaleOption);
    return () => {
      window.removeEventListener(STALE_DESIGN_OPTION_EVENT, onStaleOption);
    };
  }, [history, optionId, setOptionId]);

  const staleNoticeNode = staleNotice ? (
    <Notification
      variant="warning"
      closable
      onDismiss={() => setStaleNotice(null)}
    >
      <Small data-testid="stale-design-option-notice">
        {staleNotice === "selected"
          ? "That design option no longer exists. Switched back to Baseline."
          : "That comparison option no longer exists. Comparison was turned off."}
      </Small>
    </Notification>
  ) : null;

  if (!optionId) {
    return (
      <>
        {staleNoticeNode}
        {children}
      </>
    );
  }

  const support = getOptionPageSupport(pathname);

  return (
    <>
      {staleNoticeNode}
      <OptionGuardBody
        optionId={optionId}
        support={support}
        onSwitchToBaseline={() => setOptionId(undefined)}
      >
        {children}
      </OptionGuardBody>
    </>
  );
};

type OptionGuardBodyProps = {
  optionId: string;
  support: OptionPageSupport;
  onSwitchToBaseline: () => void;
  children: ReactNode;
};

const OptionGuardBody = ({
  optionId,
  support,
  onSwitchToBaseline,
  children,
}: OptionGuardBodyProps) => {
  const optionName = useSelectedOptionName(optionId);

  return (
    <>
      <OptionBanner support={support} optionName={optionName} />
      {support === "blocked" ? (
        <BlockedNotice onSwitchToBaseline={onSwitchToBaseline} />
      ) : (
        <OptionPageSupportContext.Provider value={support}>
          {children}
        </OptionPageSupportContext.Provider>
      )}
    </>
  );
};

export default OptionPageGuard;
